import { useState, useEffect } from 'react';
import { MATTER_STATUSES, isClosingMatterStatus } from '@/lib/matterStatus';
import { X } from '@phosphor-icons/react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import type { Matter } from '@/lib/mattersStore';
import { fetchPracticeAreas, type PracticeArea } from '@/lib/practiceAreasApi';
import { fetchCourts, type Court } from '@/lib/courtsApi';
import { fetchBarristers, type ApiBarrister } from '@/lib/barristersApi';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { ExpandableTextarea } from '@/components/ui/expandable-textarea';
import { sanitizeMoneyInput, parseMoney, formatMoney2 } from '@/lib/money';
import { ScrollLock } from '@/components/shared/ScrollLock';

interface Props {
  open: boolean;
  matter: Matter | null;
  onClose: () => void;
  onSave: (id: string, patch: Partial<Matter>) => void;
  /**
   * Take the user to the Close matter flow instead of saving a closing status here.
   *
   * Choosing "Closed settled" in this form used to end the matter on Save: open tasks
   * completed, future hearings vacated, their diary entries cancelled — with no reason, no
   * closure note, and no warning that a trial was listed next week. The close ceremony asks
   * all of that and shows the consequences first, so this form hands over to it.
   *
   * Required, so a screen that offers these statuses cannot forget to wire it up.
   */
  onCloseMatter: (id: string) => void;
}

const STATUSES = [...MATTER_STATUSES];
const FEE_TYPES = [
  { value: 'brief_fee',  label: 'Brief fee'  },
  { value: 'refresher',  label: 'Refresher'  },
  { value: 'hourly',     label: 'Hourly'     },
  { value: 'fixed',      label: 'Fixed'      },
  { value: 'conference', label: 'Conference' },
  { value: 'opinion',    label: 'Opinion'    },
  { value: 'cfa',        label: 'CFA'        },
  { value: 'dba',        label: 'DBA'        },
  { value: 'pro_bono',   label: 'Pro bono'   },
];
const RISK_RATINGS = [
  { value: 'low',    label: 'Low'    },
  { value: 'normal', label: 'Normal' },
  { value: 'high',   label: 'High'   },
  { value: 'urgent', label: 'Urgent' },
];
type Visibility = 'personal' | 'team' | 'chambers';
const VISIBILITY_OPTIONS: { value: Visibility; label: string; desc: string }[] = [
  { value: 'personal',  label: 'Personal',  desc: 'Barrister only — clerk and admin cannot see this' },
  { value: 'team',      label: 'Team',      desc: 'You + your designated clerk (default)' },
  { value: 'chambers',  label: 'Chambers',  desc: 'All chambers staff' },
];

const barristerLabel = (b: ApiBarrister) =>
  b.fullName ?? `${b.user.firstName} ${b.user.lastName}`;

const labelCls = 'mb-1.5 block text-xs font-semibold uppercase tracking-wider text-muted-foreground';
const inputCls = 'h-10 w-full rounded-md border border-border bg-card px-3 text-sm focus:border-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-100';
const selectCls = inputCls;

export function EditMatterModal({ open, matter, onClose, onSave, onCloseMatter }: Props) {
  const [practiceAreas, setPracticeAreas] = useState<PracticeArea[]>([]);
  const [courts, setCourts] = useState<Court[]>([]);
  const [barristers, setBarristers] = useState<ApiBarrister[]>([]);

  useEffect(() => {
    fetchPracticeAreas().then((p) => setPracticeAreas(p.filter((x) => x.isActive))).catch(() => {});
    fetchCourts().then((c) => setCourts(c.filter((x) => x.isActive))).catch(() => {});
    fetchBarristers().then(setBarristers).catch(() => {});
  }, []);

  const [name, setName] = useState('');
  const [number, setNumber] = useState('');
  const [practiceAreaId, setPracticeAreaId] = useState('');
  const [courtId, setCourtId] = useState('');
  const [status, setStatus] = useState('');
  const [leadBarristerId, setLeadBarristerId] = useState('');
  const [feeType, setFeeType] = useState('');
  const [feeEstimate, setFeeEstimate] = useState('');
  const [riskRating, setRiskRating] = useState('');
  const [internalNotes, setInternalNotes] = useState('');
  const [visibility, setVisibility] = useState<Visibility>('team');

  useEffect(() => {
    if (open && matter) {
      setName(matter.name);
      setNumber(matter.number);
      setPracticeAreaId(matter.practiceAreaId ?? '');
      setCourtId(matter.courtId ?? '');
      setStatus(matter.status);
      setLeadBarristerId(matter.leadBarristerId ?? '');
      setFeeType(matter.feeType);
      setFeeEstimate(String(matter.feeEstimate));
      setRiskRating(matter.riskRating);
      setInternalNotes(matter.internalNotes);
      setVisibility((matter.visibility as Visibility) ?? 'team');
    }
  }, [open, matter]);

  if (!open || !matter) return null;

  // Ending a matter is not an edit — it is handled by the close ceremony (see `onCloseMatter`).
  const closingHere = status !== matter.status && isClosingMatterStatus(status);

  const handleSave = () => {
    const patch: Partial<Matter> = {};

    if (name !== matter.name) patch.name = name;
    // The status is deliberately NOT included when it would close the matter.
    if (status !== matter.status && !closingHere) patch.status = status;
    if (feeType !== matter.feeType) patch.feeType = feeType;
    const parsedFee = parseMoney(feeEstimate);
    if (parsedFee !== matter.feeEstimate) patch.feeEstimate = parsedFee;
    if (riskRating !== matter.riskRating) patch.riskRating = riskRating;
    if (internalNotes !== matter.internalNotes) patch.internalNotes = internalNotes;
    if (visibility !== (matter.visibility ?? 'team')) patch.visibility = visibility;

    // Practice area — send the id (persists) and the name (optimistic display)
    if (practiceAreaId !== (matter.practiceAreaId ?? '')) {
      patch.practiceAreaId = practiceAreaId || null;
      patch.area = practiceAreas.find((a) => a.id === practiceAreaId)?.name ?? '';
    }

    // Court — id persists, shortName drives the list display
    if (courtId !== (matter.courtId ?? '')) {
      patch.courtId = courtId || null;
      patch.court = courts.find((c) => c.id === courtId)?.shortName ?? '';
    }

    // Lead barrister — reassign by barrister id; keep user id + display name in sync
    if (leadBarristerId !== (matter.leadBarristerId ?? '')) {
      const selected = barristers.find((b) => b.id === leadBarristerId);
      patch.leadBarristerId = leadBarristerId || null;
      patch.leadBarristerUserId = selected?.user.id ?? null;
      patch.barrister = selected ? barristerLabel(selected) : '';
    }

    // Any other edits made alongside are kept — then the close flow takes over.
    if (closingHere) {
      if (Object.keys(patch).length > 0) onSave(matter.id, patch);
      onClose();
      onCloseMatter(matter.id);
      return;
    }

    if (Object.keys(patch).length === 0) {
      onClose();
      return;
    }

    // No success message here — the save is optimistic and the server may still refuse it.
    // The parent announces the outcome once it knows (see mattersStore.updateMatter).
    onSave(matter.id, patch);
    onClose();
  };

  const practiceAreaOptions = practiceAreas.map((a) => ({ value: a.id, label: a.name }));
  const courtOptions = courts.map((c) => ({ value: c.id, label: c.shortName, sub: c.name }));
  const barristerOptions = [
    { value: '', label: '— Unassigned —' },
    ...barristers.map((b) => ({
      value: b.id,
      label: barristerLabel(b),
      sub: b.user.memberType.replace(/_/g, ' '),
    })),
  ];

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/40 p-4">
      <ScrollLock />
      <div className="flex min-h-full items-center justify-center">
      <Card className="flex w-full max-w-lg flex-col shadow-pop">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <h2 className="text-base font-semibold text-foreground">Edit matter</h2>
          <button
            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
            onClick={onClose}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <CardContent className="max-h-[70vh] space-y-4 overflow-y-auto p-6">
          {/* Name */}
          <div>
            <label className={labelCls}>Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={inputCls}
            />
          </div>

          {/* Matter number (readonly) */}
          <div>
            <label className={labelCls}>Matter number</label>
            <input
              type="text"
              value={number}
              disabled
              className={`${inputCls} cursor-not-allowed opacity-60`}
            />
          </div>

          {/* Practice area */}
          <div>
            <label className={labelCls}>Practice area</label>
            <SearchableSelect
              value={practiceAreaId}
              onChange={setPracticeAreaId}
              options={practiceAreaOptions}
              placeholder="Select practice area"
            />
          </div>

          {/* Court */}
          <div>
            <label className={labelCls}>Court</label>
            <SearchableSelect
              value={courtId}
              onChange={setCourtId}
              options={courtOptions}
              placeholder="Select court"
            />
          </div>

          {/* Status */}
          <div>
            <label className={labelCls}>Status</label>
            <select value={status} onChange={(e) => setStatus(e.target.value)} className={selectCls}>
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                </option>
              ))}
            </select>
            {closingHere && (
              <p className="mt-1 text-xs text-muted-foreground">
                Ending a matter opens the Close matter window, where you confirm the reason and
                anything still outstanding. Save changes takes you there.
              </p>
            )}
          </div>

          {/* Lead barrister */}
          <div>
            <label className={labelCls}>Lead barrister</label>
            <SearchableSelect
              value={leadBarristerId}
              onChange={setLeadBarristerId}
              options={barristerOptions}
              placeholder="Select lead barrister"
            />
            <p className="mt-1 text-[10px] text-muted-foreground">
              Reassigning updates the barrister’s access, diary and the matter timeline.
            </p>
          </div>

          {/* Fee type */}
          <div>
            <label className={labelCls}>Fee type</label>
            <select value={feeType} onChange={(e) => setFeeType(e.target.value)} className={selectCls}>
              <option value="">Select fee type</option>
              {FEE_TYPES.map((f) => (
                <option key={f.value} value={f.value}>{f.label}</option>
              ))}
            </select>
          </div>

          {/* Fee estimate */}
          <div>
            <label className={labelCls}>Fee estimate</label>
            <div className="relative">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                £
              </span>
              <input
                type="text"
                inputMode="decimal"
                value={feeEstimate}
                onChange={(e) => setFeeEstimate(sanitizeMoneyInput(e.target.value))}
                onBlur={() => setFeeEstimate(formatMoney2(feeEstimate))}
                className={`${inputCls} pl-7`}
              />
            </div>
          </div>

          {/* Risk rating */}
          <div>
            <label className={labelCls}>Risk rating</label>
            <select value={riskRating} onChange={(e) => setRiskRating(e.target.value)} className={selectCls}>
              {RISK_RATINGS.map((r) => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
          </div>

          {/* Internal notes */}
          <div>
            <label className={labelCls}>Internal notes</label>
            <ExpandableTextarea
              label="Internal notes"
              rows={4}
              value={internalNotes}
              onChange={setInternalNotes}
            />
          </div>

          {/* Visibility */}
          <div className="rounded-lg border border-border bg-muted/20 p-4">
            <label className={labelCls}>Who can see this matter</label>
            <div className="grid grid-cols-3 gap-2">
              {VISIBILITY_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setVisibility(opt.value)}
                  className={`rounded-md border p-2.5 text-left transition ${
                    visibility === opt.value
                      ? opt.value === 'personal'
                        ? 'border-red-400 bg-red-50 dark:bg-red-500/10 ring-1 ring-red-200 dark:ring-red-500/30'
                        : opt.value === 'chambers'
                          ? 'border-slate-400 bg-slate-50 dark:bg-slate-800/40 ring-1 ring-slate-200'
                          : 'border-brand-600 bg-brand-50 ring-1 ring-brand-200'
                      : 'border-border hover:border-brand-200 hover:bg-muted/40'
                  }`}
                >
                  <div className={`text-xs font-semibold ${
                    visibility === opt.value
                      ? opt.value === 'personal' ? 'text-red-700 dark:text-red-300' : opt.value === 'chambers' ? 'text-slate-700 dark:text-slate-300' : 'text-brand-700 dark:text-brand-300'
                      : 'text-foreground'
                  }`}>{opt.label}</div>
                  <div className="mt-0.5 text-[10px] text-muted-foreground">{opt.desc}</div>
                </button>
              ))}
            </div>
            {visibility === 'personal' && (
              <div className="mt-3 rounded-md border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/10 p-2.5 text-xs text-red-800 dark:text-red-300">
                <strong>Personal.</strong> Only {matter.barrister} can see this matter. Your designated clerk will not have access.
              </div>
            )}
          </div>
        </CardContent>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 border-t border-border px-6 py-4">
          <Button variant="outline" size="md" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" size="md" onClick={handleSave}>
            Save changes
          </Button>
        </div>
      </Card>
      </div>
    </div>
  );
}
